import { connect } from 'react-redux';
import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { LoadingSpinner, LoadingSpinnerSize } from '../LoadingSpinner';
import { ToxnotSignInRequiredComponent } from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import { TeamRole } from '../../../store/Models/UserContext';
import { SelectedInventoriesManager } from '../../../store/Managers/SelectedInventoriesManager';
import { RevisionDetails, SurveyType } from '../../../store/Models/Surveys/SurveyDataModel';
import RevisionRequest from '../RevisionRequest/RevisionRequest';
import React, { RefObject } from "react";
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import ExtensionRequest from '../ExtensionRequest/extensionRequest';
import CommonModalEditor from '../Common/CommonModalEditor';

import "./styles.css";
import SdsIngredientsImport from '../SdsIngredientsImport';

interface LocalState {
    loading: boolean;
}

interface LocalProps {
    pageRevisionInstructions: RevisionDetails[];
}

type BomEditorProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    LocalProps;

const BACK_TO_SURVEY_RESPONSE = "BACK_TO_SURVEY_RESPONSE";
const TRIGGER_VALIDATION = "TRIGGER_VALIDATION";
const BOM_DATA_LOADED = "BOM_DATA_LOADED";

class BomEditor extends ToxnotSignInRequiredComponent<BomEditorProps, LocalState> {
    private readonly iframeRef: RefObject<HTMLIFrameElement>;

    public constructor(props: BomEditorProps) {
        super(props);

        this.iframeRef = React.createRef();
    }

    public componentDidMount() {
        window.addEventListener('message', this.iframeComm);
        window.addEventListener('resize', this.resize);

        this.props.updateBomEditorPageVisited();
    }

    public componentWillUnmount() {
        window.removeEventListener('message', this.iframeComm);
        window.removeEventListener('resize', this.resize);
    }

    private resize = () => this.forceUpdate();

    private iframeComm = (event: MessageEvent<any>) => {
        if (event.data === BACK_TO_SURVEY_RESPONSE) {
            this.setState({
                loading: true
            });

            setTimeout(() => {
                this.setState({
                    loading: false
                });
            });
        } else if (event.data === TRIGGER_VALIDATION && this.props?.selectedInventory?.inventoryId) {
            this.props.updateValidationResult([this.props?.selectedInventory?.inventoryId]);
        }
    }

    private disableBomFieldsIfSurveyIsSubmitted() {
        const bomSpreasheetElem = $("#bom-editor-iframe").contents().find(".editor-root .product-spreadsheet-view");
        const props = this.props;

        const disableIframeEditorRoot = () => {
            if (props.coreData?.response.state === 1) { // submitted state
                $("#bom-editor-iframe").contents().find(".editor-root").addClass("disabled");
            }
        };

        const bomIframeComm = (event: MessageEvent<any>) => {
            if (event.data === BOM_DATA_LOADED) {
                disableIframeEditorRoot();
            }
        };

        if (bomSpreasheetElem === null || bomSpreasheetElem === undefined || bomSpreasheetElem.length === 0) {
            window.addEventListener('message', event => bomIframeComm(event));
        } else {
            disableIframeEditorRoot();
        }
    }

    private handleIframeLoad = () => {
        const iframe = this.iframeRef.current;

        let messageBody = JSON.stringify({
            regulationListIds: this.props?.selectedInventory?.applicableRegulatoryLists.selectMany(r => r.regulationListIds)
        });

        iframe?.contentWindow?.postMessage(messageBody, "*");
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private setLocalStorageItems() {
        localStorage.setItem("select-inventories-action", "surveyResponseImport");
        localStorage.setItem("import-survey-response-id", this.props.responseId!);
        localStorage.setItem("import-survey-response-inventory-id", this.props.selectedInventory?.inventoryId!);
        localStorage.setItem("import-survey-response-return-url", window.location.href);
    }

    private handleClickImportMaterial = () => {
        //localStorage.setItem("customer-last-active-tab", "current-tab"); //todo : find the current tab
        this.setLocalStorageItems();

        SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Select Products and Materials"));
    }

    private showHideSdsIngredientButtonOnFileCount(fileCount: number){
        //show the button only if there are multiple-sds files
        if(fileCount > 1){
            $(".import-sds-bom-button").show();
        }
    }

    private refreshBomIngredientsTable(){
        if (this.iframeRef.current) {
            this.iframeRef.current.src = this.iframeRef.current.src;
        }
    }

    protected renderConcreteComponent(): JSX.Element | null {
        this.disableBomFieldsIfSurveyIsSubmitted();
        const currentExtensionRequest = this.props.coreData?.extensionRequest;
        const canAnswer = !currentExtensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId);
        const src = `/Data/Inventory?responseId=${this.props.responseId}&inventoryId=${this.props.selectedInventory?.inventoryId}&bomEditorIframe=true&mode=EditSurveySupplierResponseBom&canAnswer=${canAnswer}`;

        return (
            <>
                <div className="page-title-border m-b-10 flex-space-between">
                    <h4 className="m-t-10 m-b-10">
                        {this.localizeString("Ingredient Disclosure Data")}
                        {!this.props.coreData?.request?.fields.some(f => f.requiredInLeafRows || f.requiredInNonSummaryNonLeafRows || f.requiredInSummaryRow) &&
                            <span className="badge m-l-5">{this.localizeString("Optional")}</span>
                        }
                    </h4>
                    <div className="import-container">
                        <div className="m-b-10">
                            {this.props.selectedInventory?.hasBomPage &&
                                <>
                                    <button className="btn btn-default m-r-5 import-sds-bom-button initially-hidden" onClick={() => { $("#import-sds-btns-modal-bom-editor").modal("show"); }}>
                                        <span className="glyphicon glyphicon-plus"></span>{this.localizeString("Add SDS Ingredients")}
                                    </button>
                                    <SdsIngredientsImport
                                        modalId="import-sds-btns-modal-bom-editor"
                                        localizer={this.props.appServices}
                                        inventoryId={this.props.selectedInventory.inventoryId}
                                        onFileCountUpdate={this.showHideSdsIngredientButtonOnFileCount}
                                        refreshParentPage={(success) => (success === true ? this.refreshBomIngredientsTable() : null)}
                                    />
                                    
                                    <button className="btn btn-default m-r-5 import-button" onClick={() => { $("#import-btns-modal-bom-editor").modal("show"); }}>{this.localizeString("Import")}
                                        <span className="import-button-icon glyphicon glyphicon-download-alt">
                                        </span>
                                    </button>
                                    <CommonModalEditor modalId={"import-btns-modal-bom-editor"} localizer={this.props.appServices} responseId={this.props.responseId} selectedInventory={this.props.selectedInventory} userId={this.getUserContextForSignedInUser().userId} navContext={undefined} handleClickImportMaterial={this.handleClickImportMaterial}></CommonModalEditor>
                                </>
                            }
                            {this.props.coreData?.request?.allowExtensionRequest && this.props.selectedInventory?.revisionRequest == null &&
                                this.props.coreData?.request?.surveyType !== SurveyType.RequestMaterialData_AllowSupplierToSpecifyMaterials &&
                                Object.entries(this.props.coreData?.response?.groupedResponseInventories).every(g => g[1].groupInventoryId !== this.props.selectedInventory?.inventoryId) &&
                                    <ExtensionRequest
                                        inventoryId={this.props.selectedInventory?.inventoryId}
                                        extensionRequest={this.props.coreData?.extensionRequest}
                                        localizer={this.props.appServices}
                                        responseId={this.props.coreData?.response.responseId}
                                        responseOwnerId={this.props.coreData?.response.ownerId}
                                        updateExtensionRequest={this.props.setExtensionRequestAndValidateProduct}
                                        validationType={this.props.selectedInventory?.validationType}>
                                    </ExtensionRequest>
                            }
                        </div>
                    </div>
                </div>
                {this.state?.loading &&
                    <div className="text-center">
                        <LoadingSpinner size={LoadingSpinnerSize.Large} />
                    </div>
                }
                {!this.state?.loading &&
                    <>
                        <div className="survey-bom-instructions" ref={this.afterRevisionsAndInstructionsRender}>
                            <MarkdownRenderer htmlText={this.localizeString("Create a list of ingredients by adding rows and selecting a Chemical Abstracts Service (CAS) Registry Number or select an import option. You can click the [eye icon](http://help.exchange.3eco.com/en/articles/2420330-marking-a-chemical-proprietary-in-a-supplier-survey) to make a CAS Number Proprietary and it will not be shared. 3E Exchange will automatically screen for regulated substances. Red asterisks indicate required fields.")} />
                        </div>
                        {this.props.pageRevisionInstructions && this.props.pageRevisionInstructions.length > 0 &&
                            <RevisionRequest key="bom-revision-request" localizer={this.props.appServices} afterRenderCallback={this.afterRevisionsAndInstructionsRender} revisionRequestInstructionsList={this.props.pageRevisionInstructions} revisionRequestTitle={this.localizeString("Revision Requests:")} extensibleColumnNames={(this.props.selectedInventory?.extensibleColumnNames ?? [])}></RevisionRequest>
                        }
                        <div className={`survey-response-bom-iframe-wrapper ${this.props.coreData?.response.state === 1 ? "survey-response-bom-disabled" : ""}`} style={{ top: this.getTopBasedOnRevisionAndInstructions() }}>
                            <iframe ref={this.iframeRef} onLoad={this.handleIframeLoad} id="bom-editor-iframe" className="bomEditorIframe" src={src} frameBorder="0"></iframe>
                        </div>
                    </>
                }
            </>
        );
    }

    private afterRevisionsAndInstructionsRender = () => {
        const top = this.getTopBasedOnRevisionAndInstructions();

        $(".survey-response-bom-iframe-wrapper").css("top", top);
    }

    private getTopBasedOnRevisionAndInstructions() {
        const revisionInstructionsContainers = $(".survey-revision-request-wrapper");
        const surveyBomInstructionsContainer = $(".survey-bom-instructions");
        const pageHeading = $(".survey-response-body-wrapper .page-title-border");

        let bomInstructionsHeight = 0;
        let revisionRequestHeight = 0;
        let pageHeadingHeight = 0;

        revisionInstructionsContainers?.each((_, object) => {
            revisionRequestHeight = revisionRequestHeight + object.clientHeight + 10; // 10 represents margin bottom of the revision wrapper
        });

        if (surveyBomInstructionsContainer.length > 0) {
            bomInstructionsHeight = surveyBomInstructionsContainer[0].clientHeight + 20; //margin bottom for BOM instructions
        }

        if (pageHeading.length > 0) {
            pageHeadingHeight = pageHeading[0].clientHeight + 10; //margin bottom for page heading
        }

        return `${revisionRequestHeight + bomInstructionsHeight + pageHeadingHeight}px`;
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(BomEditor as any);